import { useState, useEffect, useCallback } from 'react'
import {
  Typography, Table, Button, Space, Tag, message, Modal,
  Form, Input, Select, Popconfirm, Empty, Switch,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import * as userApi from '@/api/users'
import type { UserCreateForm, UserUpdateForm } from '@/api/users'
import { User } from '@/types'
import { useAuthStore } from '@/stores/auth'
import '../../../styles/aurora-page.css'
import '../../../styles/aurora-modal.css'

const { Title } = Typography
const { Option } = Select

const formatBeijing = (iso?: string | null, fallback = '从未登录') => {
  if (!iso) return fallback
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const fmt = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = fmt.formatToParts(d)
  const map: Record<string, string> = {}
  parts.forEach((p) => { if (p.type !== 'literal') map[p.type] = p.value })
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`
}

const roleLabels: Record<number, string> = {
  1: '管理员',
  2: '开发',
  3: '测试',
  4: '产品',
}

const roleColors: Record<number, string> = {
  1: 'red',
  2: 'blue',
  3: 'green',
  4: 'default',
}

export default function UserManagement() {
  const currentUser = useAuthStore((s) => s.user)
  const isAdmin = currentUser?.role === 1

  const [data, setData] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 })
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [form] = Form.useForm()

  const fetchList = useCallback(async (page = 1, pageSize = 10, searchKeyword = '') => {
    setLoading(true)
    try {
      const res = await userApi.listUsers({ page, page_size: pageSize, keyword: searchKeyword })
      setData(res.data)
      setPagination({ page: res.page, pageSize: res.page_size, total: res.total })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const handleSearch = () => {
    const nextKeyword = keyword.trim()
    setSearchKeyword(nextKeyword)
    fetchList(1, pagination.pageSize, nextKeyword)
  }

  const handleTableChange = (pagination: { current?: number; pageSize?: number }) => {
    fetchList(pagination.current, pagination.pageSize, searchKeyword)
  }

  const handleCreate = () => {
    setEditingId(null)
    form.resetFields()
    form.setFieldsValue({ role: 3 })
    setModalOpen(true)
  }

  const handleEdit = (record: User) => {
    setEditingId(record.id)
    form.setFieldsValue({
      username: record.username,
      email: record.email,
      nickname: record.nickname,
      role: record.role,
      is_active: record.is_active,
    })
    setModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await userApi.deleteUser(id)
      message.success('删除成功')
      fetchList(pagination.page, pagination.pageSize, searchKeyword)
    } catch (e) { console.error('[UserManagement] 操作失败:', e) }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)
      if (editingId) {
        const updateData: UserUpdateForm = {
          username: values.username,
          email: values.email,
          nickname: values.nickname,
        }
        // 角色/状态仅管理员可改（后端对普通用户返回 403）
        if (isAdmin) {
          updateData.role = values.role
          updateData.is_active = values.is_active
        }
        if (values.password) {
          updateData.password = values.password
        }
        await userApi.updateUser(editingId, updateData)
        message.success('更新成功')
      } else {
        await userApi.createUser(values as UserCreateForm)
        message.success('创建成功')
      }
      setModalOpen(false)
      fetchList(pagination.page, pagination.pageSize, searchKeyword)
    } catch {
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleActive = async (record: User) => {
    // 禁止禁用管理员
    if (record.role === 1 && record.is_active) {
      message.warning('管理员账号不可禁用')
      return
    }
    try {
      await userApi.updateUser(record.id, { is_active: !record.is_active })
      message.success(record.is_active ? '已禁用' : '已启用')
      fetchList(pagination.page, pagination.pageSize, searchKeyword)
    } catch (e) { console.error('[UserManagement] 操作失败:', e) }
  }

  const columns: ColumnsType<User> = [
    {
      title: '用户名', dataIndex: 'username', width: 140,
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    { title: '邮箱', dataIndex: 'email', width: 220, ellipsis: true },
    { title: '姓名', dataIndex: 'nickname', width: 120, render: (v: string) => v || '-' },
    {
      title: '角色', dataIndex: 'role', width: 100,
      render: (role: number) => <Tag color={roleColors[role]}>{roleLabels[role] || role}</Tag>,
    },
    {
      title: '状态', dataIndex: 'is_active', width: 80,
      render: (val: boolean) => (
        <Tag color={val ? 'success' : 'default'}>{val ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '最后登录', dataIndex: 'last_login_time', width: 180,
      render: (v: string | null) => formatBeijing(v),
    },
    {
      title: '创建时间', dataIndex: 'create_time', width: 180,
      render: (v: string) => formatBeijing(v, '-'),
    },
    // 操作列仅管理员可见（启用/禁用、编辑、删除均为管理员操作）
    ...(isAdmin
      ? [{
          title: '操作', width: 200, fixed: 'right' as const,
          render: (_: unknown, record: User) => (
            <Space size="small">
              <Switch
                size="small"
                checked={record.is_active}
                onChange={() => handleToggleActive(record)}
                checkedChildren="启用"
                unCheckedChildren="禁用"
              />
              <Button size="small" onClick={() => handleEdit(record)} type="link">
                编辑
              </Button>
              <Popconfirm
                title="确定删除该用户？"
                description="删除后数据将不可恢复"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button size="small" danger type="link">删除</Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          用户管理
        </Title>
        <Space>
          <Button onClick={() => fetchList(1, pagination.pageSize, searchKeyword)}>刷新</Button>
          {isAdmin && (
            <Button type="primary" onClick={handleCreate}>
              添加用户
            </Button>
          )}
        </Space>
      </div>

      <div className="search-bar">
        <Space.Compact>
          <Input
            placeholder="搜索用户名/邮箱/姓名"
            allowClear
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ minWidth: 200 }}
          />
          <Button
            type="primary"
            onClick={handleSearch}
          >
            查询
          </Button>
        </Space.Compact>
      </div>

      <div className="content-card">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100, y: 'calc(100vh - 240px)' }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          onChange={handleTableChange}
          locale={{ emptyText: <Empty description="暂无用户" /> }}
        />
      </div>

      <Modal
        title={editingId ? '编辑用户' : '添加用户'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        width={520}
        okText="保存"
        cancelText="取消"
        wrapClassName="aurora-modal-wrap"
        className="aurora-modal"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="username" label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, max: 64, message: '3-64 个字符' },
            ]}>
            <Input placeholder="请输入用户名" disabled={!!editingId} />
          </Form.Item>

          <Form.Item name="email" label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '邮箱格式不正确' },
            ]}>
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item name="nickname" label="姓名">
            <Input placeholder="请输入姓名" />
          </Form.Item>

          {isAdmin && (
            <Form.Item name="role" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
              <Select placeholder="请选择角色">
                <Option value={1}>管理员</Option>
                <Option value={2}>开发</Option>
                <Option value={3}>测试</Option>
                <Option value={4}>产品</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item name="password" label={editingId ? '新密码（留空不修改）' : '密码'}
            rules={editingId
              ? [{ min: 6, max: 64, message: '6-64 个字符' }]
              : [{ required: true, message: '请输入密码' }, { min: 6, max: 64, message: '6-64 个字符' }]}>
            <Input.Password placeholder={editingId ? '留空则不修改密码' : '请输入密码'} />
          </Form.Item>

          {editingId && isAdmin && (
            <Form.Item name="is_active" label="状态" valuePropName="checked">
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  )
}
